<script setup lang="ts">
import { computed } from 'vue';

import { useTemplate } from '@/store/templateData';
import { transformCurrencyToSymbol } from '@/utils/transformer/currency';
import { withDigitGrouping } from '@/utils/transformer/string';

import IMG_DEFAULT_PRODUCT from './assets/default-product.png';
import IMG_DEFAULT_LOGO from './assets/icon-default-store@2x.png';

interface Props {
  backgroundImage?: string;
  backgroundOffsetY?: string;
  mobileFrameImage: string;
  productImage?: string;
  msgImage?: string;
  isMsgShow?: boolean;
  price?: number;
  isShowHeaderInfo?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  backgroundImage: '',
  backgroundOffsetY: '0px',
  productImage: '',
  msgImage: '',
  isMsgShow: true,
  price: 7800,
  isShowHeaderInfo: false
});

const { handsupConfig } = useTemplate();

const currentStoreName = computed(() => handsupConfig.value?.store?.name);
const storeLogoSrc = computed(() => {
  return handsupConfig.value?.store?.avatar_url ?? IMG_DEFAULT_LOGO;
});

const currencySymbol = computed(() => {
  const currency = handsupConfig.value?.store?.currency;
  return transformCurrencyToSymbol(currency ?? '');
});
const fullPrice = computed(() => `${currencySymbol.value}${withDigitGrouping(props.price)}`);

const productSrc = computed(() => {
  return props.productImage
    ? props.productImage
    : IMG_DEFAULT_PRODUCT;
});

const wrapperStyle = computed(() => ({
  '--background-offset-y': props.backgroundOffsetY,
}));
</script>

<template>
<div class="carousel-mobile">
  <div class="carousel-mobile__inner-frame">
    <!---
      wrapper for child component can move whole block up to simulate keyboard pop up
    -->
    <div class="carousel-mobile__wrapper" :style="wrapperStyle">
      <slot name="background">
        <el-image
          class="carousel-mobile__background"
          v-if="backgroundImage"
          :src="backgroundImage"
          fit="cover"
        />
      </slot>
    </div>
  </div>
  <el-image :src="mobileFrameImage" fit="cover"/>
  <div class="carousel-mobile__inner-frame" v-if="isShowHeaderInfo">
    <!---
      wrapper for child component can move whole block up to simulate keyboard pop up
    -->
    <div class="carousel-mobile__wrapper" :style="wrapperStyle">
      <div class="carousel-mobile__store-name">{{ currentStoreName }}</div>
      <div class="carousel-mobile__logo">
        <el-image :src="storeLogoSrc" fit="contain" style="width: 20px; height: 20px;"/>
      </div>
      <div class="carousel-mobile__product">
        <el-image :src="productSrc" fit="cover"/>
        <div class="carousel-mobile__price-wrapper">
          <div class="carousel-mobile__price">{{ fullPrice }}</div>
        </div>
      </div>
    </div>
  </div>
  <transition name="fade-in">
    <el-image
      class="carousel-mobile__message"
      v-if="msgImage && isMsgShow"
      :src="msgImage"
    />
  </transition>
</div>
</template>
<style lang="scss" scoped>
$mobile-height: 436px;
$mobile-width: 215px;
$mobile-border: 8px;

$mobile-inner-padding: 8px;
$mobile-inner-border-radius: 20px;

$store-name-transform-scale: 0.7;
$store-name-top: 35px;
$store-name-left: 34px;

$store-logo-size: 22px;
$store-logo-top: 37px;
$store-logo-left: 10px;

$product-transform-scale: 0.5;
$product-size: 80px;
$product-position-right: -4px;
$product-position-top: 39px;
$product-border-radius: 4px;
$product-price-box: 20px;

$message-width: 240px;
$message-top: -26px;

.carousel-mobile {
  @apply relative;

  height: $mobile-height;
  width: $mobile-width;

  &__wrapper {
    @apply w-full h-full;

    transform: translateY(var(--background-offset-y));
  }

  &__inner-frame {
    @apply absolute overflow-hidden;

    top: $mobile-inner-padding;
    bottom: $mobile-inner-padding;
    left: $mobile-inner-padding;
    right: $mobile-inner-padding;
    border-radius: $mobile-inner-border-radius;
  }

  &__background {
    @apply w-full h-full;
  }

  &__store-name {
    @apply absolute text-white text-xs;

    top: $store-name-top;
    left: $store-name-left;
    transform: scale($store-name-transform-scale);
    transform-origin: left;
  }

  &__logo {
    @apply absolute;
    @apply flex items-center justify-center;

    top: $store-logo-top;
    left: $store-logo-left;
    width: $store-logo-size;
    height: $store-logo-size;
  }

  &__product {
    @apply absolute overflow-hidden;

    transform: scale($product-transform-scale);
    width: $product-size;
    height: $product-size;
    border-radius: $product-border-radius;
    right: $product-position-right;
    top: $product-position-top;
  }

  &__price-wrapper {
    @apply block absolute bottom-0 left-0 right-0 bg-black bg-opacity-70;
    @apply flex items-center justify-center;
    @apply text-xs text-white;

    height: $product-price-box;
  }

  &__message {
    @apply absolute left-1/2 -translate-x-1/2 transform;

    width: $message-width;
    top: $message-top;
  }

  .fade-in-enter-active {
    transition: opacity 1s, top 1s;
  }

  // vue3 的 transition 從 enter 變成 -enter-from
  .fade-in-enter-from,
  .fade-in-leave-to {
    @apply opacity-0;
  }

  .fade-in-enter-from {
    @apply top-0;
  }
}
</style>
